import { useCallback } from "react";
import { toast } from "sonner";
import { tagAdminService } from "@/features/tags/services/tags-admin.service";
import { TagBadge } from "@/features/tags";
import type { TagResponseDTO } from "@/features/tags";
import { getApiErrorMessage } from "@/shared/lib/api-error";
import { AsyncMultiSelectChips } from "./AsyncMultiSelectChips";

interface TagMultiSelectFieldProps {
  value: number[];
  onChange: (ids: number[]) => void;
  disabled?: boolean;
  inputId?: string;
}

const SEARCH_PAGE_SIZE = 8;

const getTagId = (tag: TagResponseDTO) => tag.id;
const renderTag = (tag: TagResponseDTO) => <TagBadge tag={tag} size="sm" />;

export function TagMultiSelectField({
  value,
  onChange,
  disabled,
  inputId,
}: TagMultiSelectFieldProps) {
  const searchFn = useCallback(async (term: string, signal: AbortSignal) => {
    const response = await tagAdminService.search(
      {
        name: term,
        size: SEARCH_PAGE_SIZE,
        sortBy: "name",
        sortDirection: "ASC",
      },
      { signal },
    );
    return response.content;
  }, []);

  const loadByIds = useCallback(async (ids: number[], signal: AbortSignal) => {
    const results = await Promise.all(
      ids.map((id) => tagAdminService.getById(id, { signal })),
    );
    return results;
  }, []);

  return (
    <AsyncMultiSelectChips<TagResponseDTO>
      value={value}
      onChange={onChange}
      searchFn={searchFn}
      loadByIds={loadByIds}
      getOptionId={getTagId}
      renderOption={renderTag}
      placeholder="Buscar tag pelo nome…"
      emptyText="Nenhuma tag encontrada"
      disabled={disabled}
      inputId={inputId}
      onSearchError={(e) =>
        toast.error(getApiErrorMessage(e, "Erro ao buscar tags"))
      }
      onLoadByIdsError={(e) =>
        toast.error(getApiErrorMessage(e, "Erro ao carregar tags selecionadas"))
      }
    />
  );
}
